import {
  IAccountService,
  IPropertySelection,
  IUserContext,
  ProductType,
  ServiceType,
} from "@origin-digital/event-dispatcher";
import { ServiceType as QueryParameterServiceType } from "@origin-digital/platform-enums";
import { getAccountServiceIdentifier } from "./mapAccountServices";
import { getUrlQueryParam } from "./queryParams";
import { readDataFromStorage } from "./storage";
import { mapUserDataToProperties } from "./mapProperties";

interface PropertyData {
  propertyList: IPropertySelection[];
  selectedProperty: IPropertySelection | undefined;
}

const SERVICE_PARAM_TO_PREDICATE: Record<
  QueryParameterServiceType,
  (accountService: IAccountService) => boolean
> = {
  [QueryParameterServiceType.electricity]: (accountService) =>
    accountService.serviceType === ServiceType.ELECTRICITY,
  [QueryParameterServiceType.naturalGas]: (accountService) =>
    accountService.serviceType === ServiceType.NATURAL_GAS,
  [QueryParameterServiceType.hotWater]: (accountService) =>
    accountService.serviceType === ServiceType.HOT_WATER,
  [QueryParameterServiceType.embeddedElectricity]: (accountService) =>
    accountService.serviceType === ServiceType.ELECTRICITY &&
    (accountService.productType === ProductType.CES_ELECTRICITY ||
      accountService.productType === ProductType.CES_ELECTRICITY_APPLIANCE),
  [QueryParameterServiceType.lpq]: (accountService) =>
    accountService.serviceType === ServiceType.LPG,
  [QueryParameterServiceType.internet]: (accountService) =>
    accountService.serviceType === ServiceType.BROADBAND,
};

/**
 * Copy the propertyList, and replace the given property with matching id.
 */
export const copyAndReplaceProperty = (
  propertyList: IPropertySelection[] | undefined,
  newProperty: IPropertySelection | undefined
): IPropertySelection[] | undefined => {
  if (!propertyList || !newProperty) {
    return propertyList;
  }
  return propertyList.map((property) =>
    property.id === newProperty.id ? newProperty : property
  );
};

/**
 * Apply any stored info to the property list and return the updated list and selected property.
 * Mutates propertyList items.
 */
const applyStorageToPropertyData = (
  propertyList: IPropertySelection[]
): PropertyData => {
  const storageData = readDataFromStorage();
  propertyList.forEach((property) => {
    const accountServiceIdFromStorage =
      storageData?.selectedServices?.[property.id];
    if (accountServiceIdFromStorage) {
      const accountServiceFromStorage = property.accountServices.find(
        (accountService) =>
          getAccountServiceIdentifier(accountService) ===
          accountServiceIdFromStorage
      );
      if (accountServiceFromStorage) {
        property.selectedAccountService = accountServiceFromStorage;
      }
    }
  });
  const selectedPropertyFromStorage = propertyList.find(
    (property) => property.id === storageData?.selectedProperty
  );
  return {
    selectedProperty: selectedPropertyFromStorage,
    propertyList,
  };
};

/**
 * Find property with given service.
 * If found set selectedAccountService and return updated data.
 * If not found return undefined.
 * Mutates propertyData.propertyList items.
 */
const applyServiceSelectionToPropertyData = (
  serviceSelector: (accountService: IAccountService) => boolean,
  propertyData: PropertyData
): PropertyData | undefined => {
  const { selectedProperty, propertyList } = propertyData;
  const foundProperty = selectedProperty?.accountServices.find(serviceSelector)
    ? selectedProperty
    : propertyList.find((property) =>
        property.accountServices.find(serviceSelector)
      );
  if (foundProperty) {
    foundProperty.selectedAccountService =
      foundProperty.accountServices.find(serviceSelector);
    return {
      selectedProperty: foundProperty,
      propertyList,
    };
  }
  return undefined;
};

/**
 * Find property for any URL query parameters.
 * If found, apply the selection to the property list and return the updated list and selected property.
 * Mutates propertyData.propertyList items.
 */
const applyUrlToPropertyData = (propertyData: PropertyData): PropertyData => {
  const urlAccountNumber = getUrlQueryParam("accountNumber");
  if (urlAccountNumber) {
    const accountNumberData = applyServiceSelectionToPropertyData(
      (accountService) => accountService.accountId === urlAccountNumber,
      propertyData
    );
    if (accountNumberData) {
      return accountNumberData;
    }
  }

  const urlServiceType = getUrlQueryParam("serviceType");
  const serviceTypePredicate =
    SERVICE_PARAM_TO_PREDICATE[urlServiceType as QueryParameterServiceType];
  if (serviceTypePredicate) {
    const serviceTypeData = applyServiceSelectionToPropertyData(
      serviceTypePredicate,
      propertyData
    );
    if (serviceTypeData) {
      return serviceTypeData;
    }
  }

  return propertyData;
};

/**
 * Generate the initial property selection and list of properties.
 * Takes into account the user DB data, storage data, and URL query parameters.
 */
export const getInitialPropertyData = (
  userData: IUserContext
): PropertyData => {
  const userDataProperties = mapUserDataToProperties(userData);
  const propertyData = applyStorageToPropertyData(userDataProperties);
  return applyUrlToPropertyData(propertyData);
};
